// Shared, PURE helpers behind the config-driven filter panels on the Assets and
// Work Orders tabs.
//
// No imports on purpose: this is read by two tabs and by the tests, and the
// whole of it is a transform over plain objects -- no state, no requests, no
// JSX. The option RESOLUTION (turning a declaration into a list of values) is
// not here: that is the platform's own fetchFiltersDataObject
// (Helper/Table/TableHelper.js), which every other filtered table in the
// product already uses and which this module deliberately does not duplicate.

// The declarations pageMeta holds for one table, ready to hand to
// fetchFiltersDataObject.
//
// A table with NO filterConfigurations block is the normal state of a
// half-configured org during rollout, so that answers [] -- the caller renders
// no panel and asks for nothing -- rather than throwing.
//
// `tabs` is NOT read. Every table key here maps to exactly one tab, so the key
// already scopes the filter; positional tab indices are a documented
// regression source (InventoryMain.jsx:361-371) and adding one would mean two
// places had to agree about which tab is which.
//
// Nor is anything filtered OUT. Whatever a table declares is what its panel
// offers: a page that quietly dropped a declared filter would be a config
// saying one thing and a screen showing another.
export const getDeclaredFilters = (metaInfo, tableKey) => {
    const declared = metaInfo?.filterConfigurations?.[tableKey]?.filters;
    if (!Array.isArray(declared)) return [];
    return declared.filter((filter) => filter && filter.key);
};

// fetchFiltersDataObject's output, put back into the order pageMeta declared.
//
// It resolves the dynamic filters with Promise.all and writes each key as its
// own request lands, so the object's key order is whichever endpoint answered
// first -- i.e. the panel's tab order would change between page loads. Ordering
// by the declaration is both stable AND the order the config asked for, which
// is why this does not sort by label.
//
// An entry whose `values` is not an array is dropped: MultiSelectFilter reads
// filter.values.filter(...) directly, so a declaration the resolver could not
// answer (a static filter with no staticData, say) would take the whole panel
// down with it. Missing a filter is recoverable; a crashed tab is not.
export const orderResolvedFilters = (resolved = {}, declarations = []) => {
    const ordered = {};
    (declarations || []).forEach((declaration) => {
        const entry = resolved?.[declaration?.key];
        if (entry && Array.isArray(entry.values)) {
            ordered[declaration.key] = entry;
        }
    });
    return ordered;
};

// Every value the user ticked for `key`, blanks dropped.
//
// FilterBody stores every multiSelectDropdown selection as an ARRAY of option
// ids -- there is no single-select control in the panel at all -- and the
// asset and work order filter fields take one OR many values under the same
// param name (CommaSeparatedStrList / CommaSeparatedIntList, capped at 200).
// So the whole selection travels, and the ARRAY is the canonical form: the
// per-transport encoding -- comma separated on a query string, a real JSON
// array in a POST body -- is chosen once in the Api layer.
//
// An empty selection answers [], and the mapping then sends no param at all
// rather than an empty list: the backend drops an empty list before the query
// layer precisely so that `IN ()` -- which reads as "this filter found no
// rows" rather than "no filter" -- can never be built.
export const selectedValues = (selectedFilters, key) => {
    const values = selectedFilters?.[key];
    if (!Array.isArray(values)) return [];
    return values.filter(
        (item) => item !== null && item !== undefined && item !== ""
    );
};

// The same, parsed as ids.
//
// A non-numeric selection is dropped rather than sent: it means the option
// list and the selection have drifted, and passing it through would 400 the
// whole list rather than ignoring one bad value. The rest of the selection
// still narrows.
export const selectedIDs = (selectedFilters, key) =>
    selectedValues(selectedFilters, key)
        .map((value) => parseInt(value, 10))
        .filter((value) => !Number.isNaN(value));
